/*
 * Keeps each reward coin in the cart in step with what the customer has earned.
 *
 * All of the eligibility maths lives in snippets/gold-coin-offer.liquid, because
 * only Liquid can see which collections a cart line belongs to. This file reads
 * the result from data attributes and does nothing but reconcile the difference,
 * then hands re-rendering back to the theme's own cart components via pub/sub.
 *
 * Several offers run at once, one element each, so every piece of state here is
 * keyed by reward variant. Sharing it would make the offers interfere: one
 * settling would unlock checkout while another was still mid-write, and their
 * guard signatures would collide.
 *
 * Reconciliation happens after the theme renders, never before it. Pre-adjusting
 * was tried so the cart would paint once, but it cannot be done safely: the
 * theme's updateQuantity acts on a line NUMBER, and Shopify freely renumbers the
 * cart on any write. Increasing a gift line's quantity was observed to create a
 * second line at the top rather than raise the existing one, because it splits
 * lines by discount coverage, and adding a line puts it first. Either renumbers
 * the line the theme is about to change, so it edited the wrong product. Doing
 * this properly needs the cart update taken over entirely and keyed by line key.
 *
 * Every write to the cart re-renders these elements, so a rule that lets the
 * cart's own state feed back into eligibility will oscillate. The guards below
 * bound the damage if that ever happens again: an identical reconciliation is
 * never run twice in a row, and each offer gets a budget of unsettled writes.
 */
if (!customElements.get('gold-coin-offer')) {
  const WRITE_BUDGET = 6;
  const BUSY_CLASS = 'gold-coin-offer-busy';
  const CHECKOUT_SELECTOR = '[name="checkout"]';
  /* Nothing should stay locked forever if a request hangs or a guard trips. */
  const BUSY_TIMEOUT = 10000;

  /* Elements are replaced on every cart render, so this state outlives them. */
  const pending = new Set();
  const signatures = new Map();
  const writeCounts = new Map();
  let writeLock = false;
  let busyTimer = null;

  /*
   * The lock lives on <body> because the theme replaces the cart markup on every
   * render, which would wipe a disabled attribute set here. CSS keyed off the
   * body class survives that; the attributes below are re-applied on each render
   * for keyboard and assistive-technology users, who are not stopped by
   * pointer-events alone.
   */
  const applyCheckoutLock = (locked) => {
    for (const button of document.querySelectorAll(CHECKOUT_SELECTOR)) {
      button.disabled = locked;
      button.setAttribute('aria-busy', String(locked));
    }
  };

  const isBusy = () => pending.size > 0;

  /* Checkout stays held until every offer has settled, not just the first. */
  const setPending = (key, next) => {
    if (next) pending.add(key);
    else pending.delete(key);

    const busy = isBusy();
    document.body.classList.toggle(BUSY_CLASS, busy);
    applyCheckoutLock(busy);

    clearTimeout(busyTimer);
    if (busy) {
      busyTimer = setTimeout(() => {
        console.warn('gold-coin-offer: releasing the cart lock after timeout');
        pending.clear();
        document.body.classList.remove(BUSY_CLASS);
        applyCheckoutLock(false);
      }, BUSY_TIMEOUT);
    }
  };


  class GoldCoinOffer extends HTMLElement {
    connectedCallback() {
      /* This markup is new, so re-assert the lock if anything is unsettled. */
      if (isBusy()) applyCheckoutLock(true);
      this.syncCart();
    }

    async syncCart() {
      const { variantId, required, current, lines, available, giftProperty } = this.dataset;
      const want = Number(required ?? 0);
      const have = Number(current ?? 0);
      const giftLines = GoldCoinOffer.parseLines(lines);

      if (!variantId) return;

      /*
       * This offer matches what was earned. Clear its guards: they exist to
       * stop a run of writes that never settles, and this is proof the last one
       * did. Without the reset a customer who qualifies, empties the cart and
       * qualifies again would be refused the second time on a stale signature.
       */
      if (want === have) {
        signatures.delete(variantId);
        writeCounts.delete(variantId);
        setPending(variantId, false);
        return;
      }

      /* Nothing in the cart to change, and nothing being asked for. */
      if (want === 0 && giftLines.length === 0) {
        setPending(variantId, false);
        return;
      }

      /* Never try to add a reward that cannot be bought; removal is still fine. */
      if (available === 'false' && want > have) {
        setPending(variantId, false);
        return;
      }

      /*
       * Work is needed, so hold checkout now, before the write-lock check
       * below. Registering later would let this offer look settled while
       * another one is writing, briefly re-enabling checkout.
       */
      setPending(variantId, true);

      /* One cart write at a time; the other offer syncs on the next render. */
      if (writeLock) return;

      /*
       * The same reconciliation twice without settling in between means the
       * previous write did not move the cart where Liquid expects it. Repeating
       * it would only loop.
       */
      const signature = `${want}:${have}`;
      if (signatures.get(variantId) === signature) {
        console.warn('gold-coin-offer: skipping repeat reconciliation', variantId, signature);
        setPending(variantId, false);
        return;
      }

      const writes = writeCounts.get(variantId) ?? 0;
      if (writes >= WRITE_BUDGET) {
        console.warn('gold-coin-offer: too many writes without settling', variantId);
        setPending(variantId, false);
        return;
      }

      writeLock = true;
      signatures.set(variantId, signature);
      writeCounts.set(variantId, writes + 1);

      try {
        const cart =
          giftLines.length === 0
            ? await this.addCoin(variantId, want, giftProperty)
            : await this.updateCoins(giftLines, want);
        if (!cart) return;

        /*
         * Any source other than 'cart-items' makes the theme's cart components
         * re-fetch and repaint, so no markup is built here. The repainted
         * snippet reports required === current, which ends the cycle.
         */
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gold-coin-offer', cartData: cart });
      } catch (error) {
        /* Never leave checkout locked because a request failed. */
        console.error('gold-coin-offer: could not sync the reward coin', error);
        setPending(variantId, false);
      } finally {
        writeLock = false;
      }
    }

    async addCoin(variantId, quantity, giftProperty) {
      /* Underscore-prefixed, so it stays out of the customer's view. */
      const properties = { [giftProperty || '_gold_coin_offer']: 'true' };

      const response = await fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity, properties }] }),
      });

      if (!response.ok) throw new Error(`cart/add failed: ${response.status}`);
      return response.json();
    }

    /* "index|key|qty;…" — a line key contains a colon, so these delimiters. */
    static parseLines(value) {
      return (value ?? '')
        .split(';')
        .filter(Boolean)
        .map((entry) => {
          const parts = entry.split('|');
          return { index: Number(parts[0]), key: parts[1], quantity: Number(parts[2]) };
        })
        .filter((line) => line.key && Number.isFinite(line.quantity));
    }

    /*
     * Rewrites every gift line at once rather than one of them. The reward can
     * be spread over several lines, and adjusting a single line could never
     * reach the target — the surplus stayed in the cart at full price until the
     * repeat guard gave up. The whole quantity goes on the first line and the
     * rest are zeroed, in one request so the cart is never briefly wrong.
     */
    async updateCoins(giftLines, quantity) {
      const updates = {};
      giftLines.forEach((line, index) => {
        updates[line.key] = index === 0 ? quantity : 0;
      });

      const response = await fetch(`${routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) throw new Error(`cart/update failed: ${response.status}`);
      return response.json();
    }
  }

  customElements.define('gold-coin-offer', GoldCoinOffer);
}
